<script setup lang="ts">
import { ModuleType, reportMenu, setMainMenu } from '~/types/menu'
import type { NavigationMenuItem } from '@nuxt/ui'
import type { ReportForm } from '~/stores/system'
import { isTelelaudo } from '~/utils/consts'
import type { ChatWsService } from '~/types/chat'

const { setup, loadReport, loadLogo } = useSystemStore()
const { signOut, refreshToken, updatePassword } = useAuthStore()
const { user, token } = storeToRefs(useAuthStore())
const { moduleId, homeUrl } = useRouterStore()
const $ws = useNuxtApp().$ws as ChatWsService
const userColor = computed(() => user.value?.idmedico > 0 ? 'primary' : 'error')
const chatColor = computed(() => $ws.status?.value?.toLowerCase() === 'open' ? 'primary' : 'error')
const userName = computed(() => user.value?.name)
const userMedico = computed(() => user.value?.idmedico)
const logo = ref(null)
const showPassword = ref(false)
const showChat = ref(false)
const links = ref<NavigationMenuItem[]>([])
const hasLinks = computed(() => links.value.length > 0)

const loadReports = async (): Promise<NavigationMenuItem> => {
  const report = (await loadReport() as ReportForm[]) ?? []
  const reportLinks: NavigationMenuItem[] = report.map(element => ({
    label: element.ds_caption,
    to: `/report/${element.cd_form}`
  }))
  if (reportLinks.length)
    return reportMenu(reportLinks)
  return null
}

const handleLogout = () => {
  useModal().confirm({
    title: 'Logout',
    description: 'Deseja realmente sair do sistema ?',
    okClick: () => performLogout()
  })
}

const performLogout = async () => {
  if (!await signOut())
    return
  useAuthStore().$reset()
  useSystemStore().$reset()
  $ws.close()
}

const handlePasswordUpdate = async (form) => {
  await updatePassword(form)
  showPassword.value = false
}

const handleChatConnection = () => {
  showChat.value = $ws.active
  if (!showChat.value) {
    useMessage().showError('Erro ao conectar ao chat')
  }
}

const intervalId = ref(null)
onUnmounted(() => clearInterval(intervalId.value))

onMounted(async () => {
  logo.value = await loadLogo()
  intervalId.value = setInterval(() => refreshToken().catch(() => signOut().catch(() => {})), 5 * 60 * 1000)

  if (moduleId === ModuleType.CLINUX) {
    if (!isLocalhost)
      $ws?.connect()
    links.value = user.value?.admin || user.value?.id === 1
      ? setMainMenu(moduleId)
      : !userMedico.value
          ? setMainMenu(ModuleType.CLIENT)
          : []
    if (user.value?.admin)
      loadReports().then((report: any) => {
        if (report)
          links.value.push(report)
      })
    if (userMedico.value && useRouter().currentRoute.value.path !== homeUrl)
      navigateTo(homeUrl)
  }
})

const alterarSenha = (): void => {
  if (setup?.ds_portal_azure)
    useMessage().showError('A senha deve ser alterada no Azure')
  else if (setup?.ds_portal_ldap)
    useMessage().showError('A senha deve ser alterada no LDAP')
  else
    showPassword.value = true
}

const handleHuggy = () => {
  window.Huggy?.showButton?.()
}
</script>

<template>
  <UHeader
    :to="homeUrl"
    toggle-side="left"
    :toggle="hasLinks"
  >
    <UNavigationMenu
      :items="links as NavigationMenuItem[]"
      content-orientation="vertical"
    />
    <template #title>
      <div class="bg-(--grid-chrome-bg) rounded-md px-2 py-1">
        <NuxtImg
          :src="logo"
          format="webp"
          quality="90"
          loading="eager"
          alt="Home"
          class="max-h-12 w-auto object-contain"
        />
      </div>
    </template>
    <template #body>
      <UNavigationMenu
        :items="links as NavigationMenuItem[]"
        orientation="vertical"
        class="-mx-2.5"
      />
    </template>

    <template #right>
      <ButtonTip
        v-if="userName"
        variant="link"
        square
        size="xs"
        :tooltip="userName"
        @click="alterarSenha"
      >
        <UAvatar
          :alt="userName"
          :chip="{ color: userColor }"
        />
      </ButtonTip>

      <ButtonTip
        v-if="$ws.active"
        variant="link"
        size="sm"
        tooltip="Chat"
        @click="handleChatConnection"
      >
        <UAvatar
          icon="i-mdi-chat"
          :chip-color="chatColor"
        />
      </ButtonTip>

      <UColorModeButton size="sm" />

      <ButtonTip
        v-if="isTelelaudo"
        icon="i-mdi-chat"
        color="neutral"
        variant="link"
        tooltip="Suporte"
        size="sm"
        @click="handleHuggy"
      />
      <ButtonTip
        v-if="token"
        icon="i-mdi-arrow-right"
        color="neutral"
        tooltip="Sair"
        size="sm"
        @click="handleLogout"
      />

      <!-- <SyncChat v-model:open="showChat" /> -->
    </template>
  </UHeader>
  <FormPassword
    v-if="showPassword"
    v-model:open="showPassword"
    @submit="handlePasswordUpdate"
  />
</template>
